import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  Badge,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
} from '@chakra-ui/react';
import { CreditCard } from 'lucide-react';
import { getBillingUsage, createPortalSession } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface UsageData {
  billing_mode?: 'self_serve' | 'manual_invoicing';
  posts_used: number;
  posts_included: number;
  posts_remaining: number;
  overage_posts: number;
  overage_cost_cents: number;
  image_regens: number;
  image_regen_cost_cents: number;
  month_to_date_net_cents?: number;
  billing_period_start: string | null;
  billing_period_end: string | null;
  subscription_status: string;
  tier_name: string | null;
}

export default function UsageDashboard() {
  const { userRole } = useAuth();
  const isAdminOrOwner = userRole === 'owner' || userRole === 'admin';
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    getBillingUsage()
      .then(setUsage)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !usage) {
    return <Text color="gray.500" p={4}>Loading usage data...</Text>;
  }
  if (!usage) {
    return <Text color="gray.500" p={4}>Unable to load usage data.</Text>;
  }

  const isManualInvoicing = usage.billing_mode === 'manual_invoicing';

  const pct = !isManualInvoicing && usage.posts_included > 0
    ? Math.min(100, Math.round((usage.posts_used / usage.posts_included) * 100))
    : 0;

  const statusColor = isManualInvoicing
    ? 'purple'
    : usage.subscription_status === 'active' ? 'green' :
      usage.subscription_status === 'past_due' ? 'red' : 'gray';

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <Box p={6}>
      <VStack spacing={5} align="stretch">
        <HStack justify="space-between" align="flex-start">
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="lg" fontWeight="600">Your Usage</Text>
            {isManualInvoicing ? (
              <Text fontSize="sm" color="gray.500">
                Custom plan — invoiced monthly per your contract
              </Text>
            ) : usage.tier_name && (
              <Text fontSize="sm" color="gray.500">
                {usage.tier_name} plan — {usage.posts_included} posts/month
              </Text>
            )}
          </VStack>
          <HStack>
            <Badge colorScheme={statusColor} fontSize="sm" px={2} py={1} borderRadius="md">
              {isManualInvoicing ? 'monthly invoicing' : usage.subscription_status}
            </Badge>
            {isAdminOrOwner && (
              <Button
                size="sm"
                leftIcon={<CreditCard size={14} />}
                variant="outline"
                onClick={async () => {
                  try {
                    const { url } = await createPortalSession();
                    window.location.href = url;
                  } catch { /* noop */ }
                }}
              >
                {isManualInvoicing ? 'Invoice History' : 'Manage Billing'}
              </Button>
            )}
          </HStack>
        </HStack>

        {!isManualInvoicing && (
          <Box>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="sm" color="gray.600">
                Posts: {usage.posts_used} / {usage.posts_included}
              </Text>
              <Text fontSize="sm" color="gray.500">{pct}%</Text>
            </HStack>
            <Progress
              value={pct}
              colorScheme={pct >= 90 ? 'red' : pct >= 70 ? 'yellow' : 'green'}
              size="sm"
              borderRadius="full"
            />
          </Box>
        )}

        {isManualInvoicing ? (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel>Runs this month</StatLabel>
              <StatNumber>{usage.posts_used}</StatNumber>
              <StatHelpText>graduated $1.50 / $1.30 per run</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Image Regens</StatLabel>
              <StatNumber>{usage.image_regens}</StatNumber>
              <StatHelpText>
                ${(usage.image_regen_cost_cents / 100).toFixed(2)} ($0.08 each)
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Month-to-date</StatLabel>
              <StatNumber>
                ${((usage.month_to_date_net_cents ?? 0) / 100).toFixed(2)}
              </StatNumber>
              <StatHelpText>net (pre-Stripe gross-up)</StatHelpText>
            </Stat>
          </SimpleGrid>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel>Remaining</StatLabel>
              <StatNumber>{usage.posts_remaining}</StatNumber>
              <StatHelpText>posts this period</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Overage</StatLabel>
              <StatNumber>{usage.overage_posts}</StatNumber>
              <StatHelpText>
                {usage.overage_cost_cents > 0
                  ? `$${(usage.overage_cost_cents / 100).toFixed(2)} billed`
                  : 'none'}
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Image Regens</StatLabel>
              <StatNumber>{usage.image_regens}</StatNumber>
              <StatHelpText>
                ${(usage.image_regen_cost_cents / 100).toFixed(2)}
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        <Divider />

        <Text fontSize="xs" color="gray.400">
          {isManualInvoicing
            ? `Current month: ${fmtDate(usage.billing_period_start)} — ${fmtDate(usage.billing_period_end)}. Invoice issued on the 1st of next month, Net-7.`
            : `Billing period: ${fmtDate(usage.billing_period_start)} — ${fmtDate(usage.billing_period_end)}`}
        </Text>
      </VStack>
    </Box>
  );
}
